import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { AtlBrowserStorageLocal } from '@atlassian/browser-storage-controls/web-storage-service';

import { LS_CHAT_MODE_PREFERENCE_KEY } from './nonEssentialLocalStorageKeys';
import { withNonEssentialLocalStorage } from './withNonEssentialLocalStorage';

export type PreferredChatMode = 'mini-modal' | 'sidebar';

type ChatModePreference = {
	resolve: (options: {
		currentMode?: PreferredChatMode;
		defaultMode: PreferredChatMode;
		establishPreference?: boolean;
		isMiniModalAvailable?: boolean;
		isSidebarAvailable?: boolean;
	}) => PreferredChatMode;
	set: (mode: PreferredChatMode) => Promise<void>;
};

const isPreferredChatMode = (mode: string | null): mode is PreferredChatMode =>
	mode === 'mini-modal' || mode === 'sidebar';

const isPersistentChatModeEnabled = (): boolean =>
	expVal('rovo_chat_persistent_mode', 'isEnabled', false);

const getPreferredChatMode = (): PreferredChatMode | undefined => {
	if (!isPersistentChatModeEnabled()) {
		return undefined;
	}

	try {
		const storedMode = withNonEssentialLocalStorage(LS_CHAT_MODE_PREFERENCE_KEY, () =>
			AtlBrowserStorageLocal.getItem(LS_CHAT_MODE_PREFERENCE_KEY),
		);

		return isPreferredChatMode(storedMode) ? storedMode : undefined;
	} catch {
		return undefined;
	}
};

const setPreferredChatMode = async (mode: string): Promise<void> => {
	if (!isPersistentChatModeEnabled() || !isPreferredChatMode(mode)) {
		return;
	}

	try {
		await withNonEssentialLocalStorage(LS_CHAT_MODE_PREFERENCE_KEY, () =>
			AtlBrowserStorageLocal.setItem(LS_CHAT_MODE_PREFERENCE_KEY, mode),
		);
	} catch {
		// Preferences are best-effort and must never prevent a mode switch.
	}
};

const resolvePreferredChatMode: ChatModePreference['resolve'] = ({
	currentMode,
	defaultMode,
	establishPreference = false,
	isMiniModalAvailable = true,
	isSidebarAvailable = true,
}) => {
	const preferredMode = getPreferredChatMode();

	if (currentMode) {
		return currentMode;
	}

	const requestedMode = preferredMode ?? defaultMode;
	const isRequestedModeAvailable =
		requestedMode === 'mini-modal' ? isMiniModalAvailable : isSidebarAvailable;

	if (establishPreference && preferredMode === undefined) {
		void setPreferredChatMode(defaultMode);
	}

	return isRequestedModeAvailable ? requestedMode : defaultMode;
};

export const chatModePreference: ChatModePreference = {
	resolve: resolvePreferredChatMode,
	set: setPreferredChatMode,
};
